/**
 * ENTITLEMENT-LIFECYCLE-1 Phase 1 — the super_admin entitlements screen (EL-1/2/3).
 *
 * The replacement for `scripts/seed_entitlements.py --apply`. Firms down the side,
 * the four products across, one cell per pair. The screen renders state and asks
 * for a reason; every decision about whether a grant is legal — the vocabulary,
 * idempotence, soft revoke, who may act — belongs to the server and is re-checked
 * on each call. Nothing here predicts an outcome.
 *
 * Two things it deliberately does NOT offer, because the brief's non-goals name
 * them: a "grant all" control, and any suggestion that onboarding a firm entitles
 * it to anything. A firm may take only QoE, only Valuation, or only Attest, so
 * every grant is one product at a time.
 *
 * The matrix and the confirm dialog live in @/components/admin — this file holds
 * the state and the two calls, and stays inside the FILE_SIZE_POLICY safe band.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiClient } from '@/api/client'
import { useApi } from '@/hooks/useApi'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { EntitlementMatrix } from '@/components/admin/EntitlementMatrix'
import { EntitlementActionDialog } from '@/components/admin/EntitlementActionDialog'
import { SnapshotPaymentsSection } from '@/components/admin/SnapshotPaymentsSection'
import type {
  EntitlementsResponse,
  PendingAction,
  ProductCell,
} from '@/components/admin/entitlementTypes'
import { DEFAULT_ROLE, productLabel } from '@/components/admin/entitlementTypes'

export function EntitlementsPage() {
  const { data, loading, error, execute } = useApi<EntitlementsResponse>()
  const [filter, setFilter] = useState('')
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [reason, setReason] = useState('')
  const [role, setRole] = useState(DEFAULT_ROLE)
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(() => {
    execute(() =>
      apiClient.get<EntitlementsResponse>('/v1/admin/entitlements').then(r => r.data),
    )
  }, [execute])

  useEffect(() => {
    load()
  }, [load])

  const products = data?.products ?? []
  const suggested = data?.suggested_reasons ?? []

  const firms = useMemo(() => {
    const rows = data?.firms ?? []
    const needle = filter.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter(f => f.account_id.toLowerCase().includes(needle))
  }, [data, filter])

  function openAction(
    action: 'grant' | 'revoke',
    accountId: string,
    product: string,
    cell: ProductCell,
  ) {
    setPending({ action, accountId, product })
    setReason('')
    // Re-granting keeps the role the firm last held: the common case after an
    // offboard-and-resign is the same arrangement, and a silent switch to the
    // default would quietly change what that firm's users can do.
    setRole(action === 'grant' ? (cell.role || DEFAULT_ROLE) : DEFAULT_ROLE)
  }

  async function submit() {
    if (!pending) return
    const trimmed = reason.trim()
    if (!trimmed) {
      // The server refuses this too (422). Checking here as well so the operator
      // is told in place rather than by a failed round trip.
      toast.error('A reason is required.')
      return
    }
    setSubmitting(true)
    try {
      if (pending.action === 'grant') {
        await apiClient.post('/v1/admin/entitlements/grant', {
          account_id: pending.accountId,
          product: pending.product,
          role,
          reason: trimmed,
        })
        toast.success(`${productLabel(pending.product)} granted to ${pending.accountId}.`)
      } else {
        await apiClient.post('/v1/admin/entitlements/revoke', {
          account_id: pending.accountId,
          product: pending.product,
          reason: trimmed,
        })
        toast.success(`${productLabel(pending.product)} revoked from ${pending.accountId}.`)
      }
      setPending(null)
      load()
    } catch {
      toast.error(pending.action === 'grant' ? 'Failed to grant.' : 'Failed to revoke.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading entitlements…</div>
  if (error) return <div className="p-6 text-sm text-red-600">Failed to load entitlements.</div>

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Product Entitlements</h1>
        <p className="mt-1 text-sm text-gray-500">
          Which firms hold which products. Granting is deliberate and per product — a
          firm may take one and not the others. Every change is audited with your
          identity and reason.
        </p>
      </div>

      <div className="mb-4 max-w-sm">
        <Label htmlFor="firm-filter" className="sr-only">
          Filter firms
        </Label>
        <Input
          id="firm-filter"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter firms…"
          data-testid="entitlements-filter"
        />
      </div>

      {firms.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="entitlements-empty">
          {data?.firms?.length ? 'No firms match that filter.' : 'No firms found.'}
        </p>
      ) : (
        <EntitlementMatrix firms={firms} products={products} onAction={openAction} />
      )}

      <SnapshotPaymentsSection />

      <EntitlementActionDialog
        pending={pending}
        reason={reason}
        onReasonChange={setReason}
        role={role}
        onRoleChange={setRole}
        suggested={suggested}
        submitting={submitting}
        onCancel={() => setPending(null)}
        onConfirm={submit}
      />
    </div>
  )
}

export default EntitlementsPage
